import { type CollectionBenchmarkPickerEntry, type ImageType, useGetCollectionBenchmarkPickerQuery } from '@vizit/api';
import { type SearchBenchmarksQueryVariables, toBenchmarkPreviewUrls, useSearchBenchmarksQuery } from '@vizit/api';
import type { BenchmarkPreviewUrl } from '@vizit/api';
import { UNIVERSAL_BENCHMARK_KEY } from '@vizit/constants';
import { useShowUniversalBenchmarks } from '@vizit/feature-flags';
import { type BenchmarkOption, VIZIT_BENCHMARK_NAMES, isVizitRetailer } from '@vizit/organisms';
import * as React from 'react';

/**
 * One picker entry as a `BenchmarkOption`. The route has already narrowed custom sets to the collection's image
 * type and decided `disabled`, so neither is derived here; the custom sets are stamped with that type (`scope`)
 * and the Vizit ones stay `null`, which means they score every type. The route carries no preview thumbnails, so a
 * custom set's mosaic takes `previewUrls` from the workspace's benchmark list; without them its cells stay empty.
 */
function toPickerBenchmarkOption(
  entry: CollectionBenchmarkPickerEntry,
  scope?: ImageType,
  previewUrls?: BenchmarkPreviewUrl[],
): BenchmarkOption {
  const retailer = entry.retailer && isVizitRetailer(entry.retailer) ? entry.retailer : undefined;
  return {
    kind: entry.kind,
    id: entry.id ?? undefined,
    retailer,
    // A Vizit benchmark reads as it does wherever a scored pair names it, whatever the route calls it.
    name: entry.kind === 'vizit' && retailer ? VIZIT_BENCHMARK_NAMES[retailer] : entry.name,
    scope: entry.kind === 'custom' ? (scope ?? null) : null,
    imageCount: entry.imageCount,
    minimumImages: entry.minimumImages,
    disabled: entry.disabled,
    description: entry.description,
    recommendedImages: entry.recommendedImages,
    previewUrls: entry.kind === 'custom' ? (previewUrls ?? []) : undefined,
  };
}

/**
 * Whether the picker can offer the entry. A Vizit benchmark is addressed by its retailer, so one the picker has no
 * retailer for is left out rather than listed as a row nothing can score against; so is Universal while its flag is off.
 */
function isOfferedEntry(entry: CollectionBenchmarkPickerEntry, showUniversalBenchmarks: boolean): boolean {
  if (entry.kind === 'custom') return true;
  if (entry.retailer === UNIVERSAL_BENCHMARK_KEY) return showUniversalBenchmarks;
  return !!entry.retailer && isVizitRetailer(entry.retailer);
}

/**
 * How long the workspace list answers the picker's previews before it is read again. Stated here rather than left to
 * the app's default, so the picker reuses the availability check's answer instead of fetching the list a second time.
 */
const BENCHMARK_LIST_STALE_TIME = 5 * 60 * 1000;

interface UseCollectionBenchmarkOptionsProps {
  workspaceId?: string;
  collectionId: string;
  /** The collection's image type; the custom sets the route lists are of this type and are marked with it */
  scope?: ImageType;
  /** Reads nothing while false, e.g. while the feature is off */
  enabled?: boolean;
}

/**
 * The benchmarks a collection can be scored against, as the benchmark picker lists them: the Vizit benchmarks
 * first (those it has a retailer for, Universal only behind its flag), then the workspace's custom sets of the
 * collection's image type, in the API's order. Each custom set's mosaic comes from the workspace's benchmark list (the
 * 64px resize, else the original), read with the same variables as the custom benchmarks availability check so the
 * page reuses that answer; the options do not wait for it, and a set the list does not have, or a list that fails,
 * leaves empty cells.
 */
function useCollectionBenchmarkOptions({
  workspaceId,
  collectionId,
  scope,
  enabled = true,
}: UseCollectionBenchmarkOptionsProps) {
  const showUniversalBenchmarks = useShowUniversalBenchmarks();
  const { data, isLoading, isError, refetch } = useGetCollectionBenchmarkPickerQuery(
    { workspace_id: workspaceId ?? '', collection_id: collectionId },
    { enabled: enabled && !!workspaceId && !!collectionId },
  );

  const listVariables: SearchBenchmarksQueryVariables = {
    workspace_id: workspaceId ?? '',
    sort_by: 'created_at',
    sort_direction: 'desc',
  };
  const { data: benchmarkList } = useSearchBenchmarksQuery(listVariables, {
    enabled: enabled && !!workspaceId,
    staleTime: BENCHMARK_LIST_STALE_TIME,
  });

  const previewUrlsById = React.useMemo(
    () =>
      new Map(
        (benchmarkList?.data ?? []).map((benchmark) => [
          benchmark.id,
          toBenchmarkPreviewUrls(benchmark.previews ?? []),
        ]),
      ),
    [benchmarkList],
  );

  const options = React.useMemo<BenchmarkOption[]>(
    () =>
      (data?.groups ?? []).flatMap((group) =>
        group.benchmarks
          .filter((entry) => isOfferedEntry(entry, showUniversalBenchmarks))
          .map((entry) => toPickerBenchmarkOption(entry, scope, entry.id ? previewUrlsById.get(entry.id) : undefined)),
      ),
    [data, scope, previewUrlsById, showUniversalBenchmarks],
  );

  // Reading again after a failure goes back to loading, since there is no data to keep showing.
  const retry = React.useCallback(() => void refetch(), [refetch]);

  // A disabled query reports isLoading false, so a workspace that has not resolved yet counts as loading.
  return { options, isLoading: enabled && (isLoading || !workspaceId), isError, retry };
}

export { toPickerBenchmarkOption, useCollectionBenchmarkOptions };
